import { useEffect, useState } from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, identityApiRef } from '@backstage/core-plugin-api';
import { InfoCard, Progress } from '@backstage/core-components';
import {
  Button,
  Grid,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { vaultApiRef } from '../../api/VaultApi';
import { useVaultRequest } from '../../hooks/useVaultRequest';
import { useLiveUpdates } from '../../hooks/useLiveUpdates';

const useStyles = makeStyles(theme => ({
  section: { marginBottom: theme.spacing(2) },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 600,
    color: '#888',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  dangerRow: {
    border: '1px solid #ffebee',
    borderRadius: 6,
    padding: 12,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  actionTitle: { fontWeight: 500, fontSize: 13 },
  actionDesc: {
    fontSize: 11,
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
}));

export function EntityVaultRequestAdminCard() {
  const { entity } = useEntity();
  const vaultApi = useApi(vaultApiRef);
  const identityApi = useApi(identityApiRef);
  const requestId = entity.metadata.annotations?.['vault.ice.com/request-id'];
  const { request, loading, reload } = useVaultRequest(requestId);
  const classes = useStyles();
  useLiveUpdates(requestId ? `vault:request:${requestId}` : undefined, reload);

  const [isAdmin, setIsAdmin] = useState<boolean | undefined>();
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [abandonError, setAbandonError] = useState('');

  useEffect(() => {
    identityApi.getProfileInfo().then(profile => {
      const userEmail = profile.email ?? '';
      setEmail(userEmail);
      if (userEmail) {
        vaultApi
          .checkAdmin(userEmail)
          .then(r => setIsAdmin(r.isAdmin))
          .catch(() => setIsAdmin(false));
      }
    });
  }, [identityApi, vaultApi]);

  if (loading || isAdmin === undefined) return <Progress />;
  if (!isAdmin) {
    return (
      <InfoCard title="Admin">
        <Typography color="textSecondary">Admin access required.</Typography>
      </InfoCard>
    );
  }
  if (!request)
    return (
      <InfoCard title="Admin">
        <Typography color="textSecondary">Request not found.</Typography>
      </InfoCard>
    );

  const handleAbandon = async () => {
    if (!requestId) return;
    setAbandonError('');
    try {
      await vaultApi.abandonRequest(
        requestId,
        email,
        note || 'Admin abandoned',
      );
    } catch (err: any) {
      setAbandonError(err.message ?? 'Failed to abandon');
    }
  };

  return (
    <InfoCard title="Admin" subheader="Role-gated — only visible to admins">
      <div className={classes.section}>
        <Grid
          container
          spacing={1}
          style={{ background: '#f8f8f8', borderRadius: 4, padding: 8 }}
        >
          <Grid item xs={6}>
            <Typography variant="caption" color="textSecondary">
              State:
            </Typography>{' '}
            <b>{request.state.toUpperCase()}</b>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="textSecondary">
              BSN:
            </Typography>{' '}
            {request.bsn}
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="textSecondary">
              Requester:
            </Typography>{' '}
            {request.requester}
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="textSecondary">
              CHG:
            </Typography>{' '}
            {request.chgNumber ?? '—'}
          </Grid>
        </Grid>
      </div>
      <div className={classes.section}>
        <div className={classes.sectionTitle}>Manual Actions</div>
        <div className={classes.dangerRow}>
          <div>
            <div className={classes.actionTitle} style={{ color: '#c62828' }}>
              Abandon request
            </div>
            <div className={classes.actionDesc}>
              Terminal. No further processing.
            </div>
          </div>
          <Button
            variant="contained"
            style={{ background: '#e53935', color: 'white' }}
            onClick={handleAbandon}
          >
            Abandon
          </Button>
        </div>
        {abandonError && (
          <Typography color="error" variant="caption">
            {abandonError}
          </Typography>
        )}
      </div>
      <div className={classes.section}>
        <div className={classes.sectionTitle}>Note for trail</div>
        <TextField
          fullWidth
          multiline
          rows={3}
          variant="outlined"
          size="small"
          placeholder="Reason for manual action…"
          value={note}
          onChange={e => setNote(e.target.value)}
        />
      </div>
    </InfoCard>
  );
}
